import { useCallback, useMemo, useRef, useState } from 'react';
import { currencyGlyph } from '@/lib/money/index.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { fetchWalletBalances, WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { betLimitsOf } from '../state/bet.js';
import { BetPanel } from '../bet-panel/BetPanel.js';
import { useIsImpersonating } from '@/features/auth/useIsImpersonating.js';
import { useGuestGate } from '../useGuestGate.js';
import { GameInfoBar } from './GameInfoBar.js';
import { BelowGame } from './BelowGame.js';
import { ProvablyFairPanel } from '../ProvablyFairPanel.js';
import { HowToPlay } from '../HowToPlay.js';
import { useGameSound } from '../effects/sound.js';
import { useMoney } from '../../../lib/money/useMoney.js';
import { minorToPlain } from '../state/bet.js';
import {
  PlinkoScene,
  bucketColorCss,
  formatMultiplierLabel,
  type PlinkoSceneHandle,
} from '../scenes/plinko/PlinkoScene.js';
import { PlinkoRecentHits, pushRecentHit, type RecentHit } from '../scenes/plinko/PlinkoRecentHits.js';
import { usePlinkoDrops, type PlinkoDrop } from '../scenes/plinko/usePlinkoDrops.js';
import type { OriginalsConfig, ParamValue } from '../types.js';
import type { RoundResult } from '../bet-panel/auto/useAutoBet.js';

interface Props {
  config: OriginalsConfig;
  params: Record<string, ParamValue> | undefined;
  onParamsChange: (p: Record<string, ParamValue>) => void;
  configStale?: boolean;
}

/**
 * Plinko's concurrent-drop view. Unlike {@link SingleShotGameView} (single-flight via
 * useOriginalsPlay), every Bet click here fires an independent server round through
 * {@link usePlinkoDrops} WITHOUT locking the button — many balls animate at once and each
 * resolves on its own settle. This concurrency is scoped to Plinko only; the shared hook and
 * all other games are untouched.
 */
export function PlinkoGameView({ config, params, onParamsChange, configStale = false }: Props) {
  const { t } = useTranslation('originals');
  const isImpersonating = useIsImpersonating();
  const guest = useGuestGate();
  const sound = useGameSound();
  const stageRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<PlinkoSceneHandle>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [fairnessOpen, setFairnessOpen] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [lastRoundId, setLastRoundId] = useState<string | undefined>(undefined);
  const [recentHits, setRecentHits] = useState<RecentHit[]>([]);
  const { multiplier } = useMoney();
  const plinkoCurrency = useMoneyCurrency();

  const limits = useMemo(() => betLimitsOf(config), [config]);
  const [bet, setBet] = useState<bigint>(() => limits.min);

  const effectiveParams = (params ?? config.resolvedParams) as Record<string, ParamValue>;
  const setParam = useCallback(
    (key: string, value: ParamValue) => onParamsChange({ ...effectiveParams, [key]: value }),
    [effectiveParams, onParamsChange],
  );

  // Balance after the most recently SETTLED drop (visual landing), falling back to the wallet
  // query. Tracking by settle (not by network response) keeps the number in step with what the
  // player sees land, even while other balls are still mid-air.
  const [settledBalance, setSettledBalance] = useState<bigint | null>(null);
  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    staleTime: 15_000,
    enabled: !guest.isGuest,
  });
  const balance: bigint | null =
    settledBalance ??
    (walletQuery.data
      ? typeof walletQuery.data.balance === 'number'
        ? BigInt(Math.round(walletQuery.data.balance))
        : BigInt(walletQuery.data.balance)
      : null);

  const presenterBuckets = useCallback(
    () => (config.presenterConfig as { buckets?: number[] } | null)?.buckets,
    [config.presenterConfig],
  );
  const drops = usePlinkoDrops(config.gameCode, presenterBuckets);

  // Resolve map: each fired drop's RoundResult promise (for auto-bet) resolves when that
  // specific ball settles in the scene — keyed by roundId so concurrent balls don't cross.
  const pending = useRef<Map<string, (r: RoundResult) => void>>(new Map());

  const handleSettle = useCallback(
    (settled: PlinkoDrop) => {
      setSettledBalance(BigInt(settled.envelope.balanceAfter));
      setLastRoundId(settled.envelope.roundId);
      const total = (presenterBuckets()?.length ?? 0) || settled.bucket + 1;
      setRecentHits((prev) =>
        pushRecentHit(prev, {
          id: settled.id,
          label: formatMultiplierLabel(settled.multiplierBp),
          color: bucketColorCss(settled.multiplierBp, settled.bucket, total),
          dim: settled.multiplierBp <= 0,
        }),
      );
      drops.onSettled(settled.id);
      const resolve = pending.current.get(settled.id);
      if (resolve) {
        pending.current.delete(settled.id);
        resolve({ win: settled.envelope.win, payout: BigInt(settled.envelope.payout) });
      }
    },
    [drops, presenterBuckets],
  );

  // BetPanel contract: playRound resolves after the round "reveals". For Plinko a round reveals
  // when its ball lands. Manual clicks ignore the promise; auto-bet awaits it to pace its loop.
  const playRound = useCallback(
    (b: bigint): Promise<RoundResult> =>
      new Promise<RoundResult>((resolve, reject) => {
        sound.play('click');
        void drops.drop(b, effectiveParams).then((d) => {
          if (!d) {
            reject(new Error('plinko-drop-failed'));
            return;
          }
          pending.current.set(d.id, resolve);
          const payout = BigInt(d.envelope.payout);
          sceneRef.current?.addDrop(
            {
              id: d.id,
              path: d.path,
              bucket: d.bucket,
              multiplierBp: d.multiplierBp,
              winLabel:
                payout > 0n
                  ? `+${currencyGlyph(plinkoCurrency)}${minorToPlain(payout, multiplier())}`
                  : undefined,
            },
            () => handleSettle(d),
          );
        });
      }),
    [drops, effectiveParams, handleSettle, sound, multiplier],
  );

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1180px] mx-auto">
      {configStale && (
        <p className="text-[12px] text-[var(--warning,var(--danger))] text-center" role="status">
          {t('configStale')}
        </p>
      )}
      <Card className="!p-0 overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-[340px] lg:shrink-0 order-2 lg:order-1 p-4 bg-[var(--og-rail)] border-t lg:border-t-0 lg:border-r border-[var(--border)]">
            <BetPanel
              config={config}
              limits={limits}
              bet={bet}
              onBet={setBet}
              params={effectiveParams}
              onParam={setParam}
              balance={balance}
              busy={false}
              error={drops.error}
              playRound={playRound}
              supportsAuto
              selection={undefined}
              selectionValid
              options={null}
              onAutoRunningChange={setAutoRunning}
              holdToRepeat
              hideCta={isImpersonating}
              onGuestPlay={guest.isGuest ? guest.signIn : undefined}
              ctaHint={t('plinko.holdHint')}
            />
          </div>
          <div className="og-stage flex-1 min-w-0 order-1 lg:order-2 flex flex-col">
            <div ref={stageRef} className="relative w-full overflow-hidden">
              <PlinkoScene
                // Rebuild on the RESOLVED config (tracks the debounced fetch) so a rows/risk change
                // remounts the board once, in step with the new presenterConfig — not on the immediate
                // param state ahead of the config it reads.
                key={`${config.gameCode}:${JSON.stringify(config.resolvedParams)}`}
                ref={sceneRef}
                config={config}
                turbo={autoRunning}
              />
              <PlinkoRecentHits hits={recentHits} />
            </div>
          </div>
        </div>
      </Card>

      <GameInfoBar
        code={config.gameCode}
        title={config.name}
        muted={sound.muted}
        onToggleSound={sound.toggleMute}
        onFairness={() => setFairnessOpen((v) => !v)}
        onHowTo={() => setGuideOpen(true)}
      />

      {fairnessOpen && <ProvablyFairPanel refreshKey={lastRoundId} roundId={lastRoundId} />}

      <BelowGame />

      <HowToPlay config={config} open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
